/* eslint-disable */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import TextLink from 'components/TextLink';
import FormActionBar from 'components/FormActionBar';
import { Btn } from 'components/buttons';
import { updateProfile } from 'redux/modules/user';
import styled from '@emotion/styled';
import { Form, FormActionTypes } from '@entrata/portals-common/lib/components/forms';
import { Dialog } from 'components/modals';
import { TextField, EmailField, PhoneField } from '@entrata/portals-common/lib/standard-fields';
import Message from 'components/Message';
import { removeError } from 'redux/modules/errors';
import formatPhoneNumber from 'utils/formatPhoneNumber';

const StyledH2 = styled('h2')`
  color: ${({ theme }) => theme.gray.base};
  font-size: 18px;
  font-family: ${({ theme }) => theme.fonts.soehneKraftig};
`;

const StyleFormField = styled(Form.Field)`
  margin-bottom: 24px;
  flex-grow: 0;
  label {
    color: ${({ theme }) => theme.text.dark};
    font-size: 14px;
    font-family: ${({ theme }) => theme.fonts.soehneBuch};
  }
  input {
    color: ${({ theme }) => theme.text.dark};
    border-radius: 5px;
    font-family: ${({ theme }) => theme.fonts.soehneBuch};
    margin-bottom: 0;
  }
`;

const StyledFormActionBar = styled(FormActionBar)`
  padding-top: 24px;
`;

const EditProfile = ({
  employeeId,
  userId,
  entityId,
  updateProfile,
  nameFirst,
  nameLast,
  emailAddress,
  phoneNumber,
  onClose,
  show,
  updateProfileSuccess,
  updateProfileError,
  removeError,
  homebodyUserId,
}) => {
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const handleClose = () => {
    setLoading(false);
    setIsDisabled(true);
    !!updateProfileSuccess && removeError('updateProfileSuccess');
    !!updateProfileError && removeError('updateProfileError');
    onClose();
  };

  const onSubmit = ({ values }) => {
    updateProfile({
      ...values,
      userId,
      employeeId,
      homebodyUserId,
      phoneNumber: formatPhoneNumber(values.phoneNumber),
    });
    setLoading(true);
    setIsDisabled(true);
  };

  const getInitialFieldState = () => ({
    nameFirst: { value: nameFirst, required: true },
    nameLast: { value: nameLast, required: true },
    emailAddress: { value: emailAddress, required: true },
    phoneNumber: { value: phoneNumber, required: true },
  });

  const fieldStateReducer = ({ state, changes: { type, payload, name } }) => {
    const updatedFieldState = { ...state, ...payload };
    if (type === FormActionTypes.onChange) {
      !!updateProfileSuccess && removeError('updateProfileSuccess');
      !!updateProfileError && removeError('updateProfileError');
      handleFieldValueChange(updatedFieldState);
    }

    if (type === FormActionTypes.onFocus && name === 'phoneNumber') {
      updatedFieldState.phoneNumber.value = updatedFieldState.phoneNumber?.value.replace(/\D/g, '');
    }

    return updatedFieldState;
  };

  const handleFieldValueChange = (fields) => {
    let dirtyFields = Object.values(fields).filter((field) => field.dirty);
    setIsDisabled(!!!dirtyFields.length);
  };

  if (loading && (!!updateProfileSuccess || !!updateProfileError)) {
    setLoading(false);
  }

  return (
    <Dialog title='Edit Profile' onClose={handleClose} show={show}>
      {(!!updateProfileError || !!updateProfileSuccess) && (
        <Message type={updateProfileError ? 'error' : 'success'} show={true}>
          {updateProfileError || updateProfileSuccess}
        </Message>
      )}
      <StyledH2>Contact Info</StyledH2>
      <Form initialFieldState={getInitialFieldState} fieldStateReducer={fieldStateReducer} onSubmit={onSubmit}>
        <Form.Row widths={['50%', '50%']}>
          <StyleFormField name='nameFirst'>
            {(props) => <TextField {...props} label='First Name' disabled={!employeeId} />}
          </StyleFormField>
          <StyleFormField name='nameLast'>
            {(props) => <TextField {...props} label='Last Name' disabled={!employeeId} />}
          </StyleFormField>
        </Form.Row>
        <Form.Row>
          <StyleFormField name='emailAddress'>{(props) => <EmailField {...props} label='Email' />}</StyleFormField>
        </Form.Row>
        <Form.Row widths={['50%']}>
          <StyleFormField name='phoneNumber'>{(props) => <PhoneField {...props} label='Phone' />}</StyleFormField>
        </Form.Row>
        <Form.Actions>
          {({ hasErrors }) => (
            <StyledFormActionBar>
              <TextLink type='reset' onClick={handleClose}>
                Nevermind
              </TextLink>
              <Btn
                appearance='primary'
                type='submit'
                disabled={hasErrors || !!updateProfileSuccess || isDisabled}
                loading={loading}
              >
                Save Changes
              </Btn>
            </StyledFormActionBar>
          )}
        </Form.Actions>
      </Form>
    </Dialog>
  );
};

EditProfile.propTypes = {
  nameFirst: PropTypes.string,
  nameLast: PropTypes.string,
  emailAddress: PropTypes.string,
  phoneNumber: PropTypes.string,
  employeeId: PropTypes.number,
  entityId: PropTypes.number,
  userId: PropTypes.number,
  updateProfile: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  updateProfileSuccess: PropTypes.string,
  updateProfileError: PropTypes.string,
  removeError: PropTypes.func,
  homebodyUserId: PropTypes.number,
};

EditProfile.defaultProps = {
  nameFirst: '',
  nameLast: '',
  emailAddress: '',
  phoneNumber: '',
};

export default connect(
  ({
    user: { nameFirst, nameLast, email, phoneNumber, entityId, homebodyUserId },
    getQuote: { user, employee },
    errors: { updateProfileSuccess, updateProfileError },
  }) => ({
    nameFirst,
    nameLast,
    emailAddress: email,
    phoneNumber,
    entityId,
    userId: user,
    employeeId: employee.id,
    updateProfileSuccess,
    updateProfileError,
    homebodyUserId,
  }),
  { updateProfile, removeError }
)(EditProfile);
